// =============================================================================
// services/livekit-globals.ts — lazy, off-boot WebRTC global registration.
//
// WHY THIS EXISTS (launch-crash fix, 1.4.2/64):
//   @livekit/react-native's `registerGlobals()` forces the native
//   @livekit/react-native-webrtc `WebRTCModule` to instantiate, which builds the
//   WebRTC RTCPeerConnectionFactory + AVAudioEngine audio device in its ObjC
//   `-init`. On bridgeless New Architecture that native init faults at app
//   launch — a NATIVE crash that a JS try/catch cannot catch — so the app
//   "doesn't even open". It MUST NOT run at boot.
//
//   So this is NOT imported by index.js or anything on the boot graph. It is
//   imported ONLY by the lazy-loaded call/room screens (VoiceCallScreen,
//   VoiceRoomScreen — registered via `getComponent` in App.tsx), so
//   `registerGlobals()` runs the first time a user opens a call/room, never at
//   launch. Idempotent + crash-safe: a failure disables calling instead of
//   taking down the screen.
// =============================================================================
import { Platform } from 'react-native';
import {
  registerGlobals,
  AudioSession,
  AndroidAudioTypePresets,
} from '@livekit/react-native';

let registered = false;

/**
 * Register WebRTC globals for LiveKit. Safe to call repeatedly; the underlying
 * native init runs at most once. Call this before mounting <LiveKitRoom /> or
 * using livekit-client. Returns true if globals are (now) registered.
 */
export function ensureLiveKitGlobals(): boolean {
  if (registered) return true;
  try {
    // autoConfigureAudioSession:false — we manage the AVAudioSession MANUALLY via
    // prepareVoiceAudio() (configure→setApple→start). Letting the SDK ALSO
    // auto-configure it (the default) runs TWO audio-session managers against the
    // same AVAudioSession → the documented dual-management conflict (LiveKit audio
    // routing issue #322) that reads as "connected but no audio / stuck". Disable
    // the auto path so ours is the single source of truth.
    registerGlobals({ autoConfigureAudioSession: false });
    registered = true;
  } catch (e) {
    console.warn('[CRYB] LiveKit registerGlobals failed; voice/video disabled', e);
  }
  return registered;
}

// =============================================================================
// Voice audio-session lifecycle (AUDIO-DEAD fix)
//
// ROOT CAUSE: the screens used to call AudioSession.startAudioSession() BARE.
// On iOS that is just `AVAudioSession.setActive(true)` (see the SDK source,
// @livekit/react-native/ios/LiveKitReactNativeModule.swift startAudioSession) —
// it does NOT set the session category. Unless the session is explicitly put in
// `playAndRecord`, the mic capture path never comes up AND playback sits in an
// ambient-style category that the ring/silent switch mutes → silent both ways
// while video (which doesn't touch AVAudioSession) flows fine. The SDK normally
// covers this via setupIOSAudioManagement() (installed by registerGlobals),
// but that path is driven by native AudioDeviceModule engine events over the
// event emitter — a round trip that is not reliable on our bridgeless/legacy-
// interop setup. The SDK's own guidance (issue livekit/client-sdk-react-native
// #322 + AudioSession.configureAudio docs) is: apply the Apple audio
// configuration explicitly BEFORE connecting to the room.
//
// CANONICAL SEQUENCE (must run before <LiveKitRoom connect> mounts):
//   1. AudioSession.configureAudio(...)            — template for WebRTC's own
//      session config (android presets + iOS default output),
//   2. AudioSession.setAppleAudioConfiguration(...) — applies category/mode to
//      the AVAudioSession NOW (playAndRecord + voiceChat/videoChat),
//   3. AudioSession.startAudioSession()             — setActive(true).
// =============================================================================

/** Which live surface is asking for audio — decides mode + default output. */
export type VoiceAudioProfile =
  | 'videoCall' // 1:1 video call  → videoChat, speaker default
  | 'audioCall' // 1:1 audio call  → voiceChat, earpiece default (phone-like)
  | 'room'; // live voice room → voiceChat, speaker default

let audioSessionActive = false;
// Bumped on every prepare; lets a DELAYED stop no-op if a newer live surface
// has re-activated the session in the meantime (end call → open room fast).
let audioGeneration = 0;
// In-flight teardown promise (null when idle). A new prepareVoiceAudio() awaits
// this so a fresh call/room never configures/activates the AVAudioSession while a
// previous surface's stop is still running — the concurrent
// activate-vs-deactivate race is the "end call → open room fast" audio-dead /
// double-stop crash family. Set by stopVoiceAudio(), cleared when it settles.
let teardownInProgress: Promise<void> | null = null;

/**
 * Configure + activate the native audio session for a live surface. Idempotent
 * and crash-safe (a failure logs and the room still connects — worst case the
 * OS default routing applies). Call BEFORE mounting <LiveKitRoom>.
 */
export async function prepareVoiceAudio(profile: VoiceAudioProfile): Promise<void> {
  // If a previous surface is still tearing down its audio session, wait for that
  // to settle before we configure/activate — otherwise our setActive(true) races
  // its setActive(false) (concurrent call/room race). Bounded: stopVoiceAudio is
  // itself crash-safe and the teardown guard caps how long a stop can run.
  if (teardownInProgress) {
    try {
      await teardownInProgress;
    } catch {
      /* a failed teardown must never block a new prepare */
    }
  }
  audioGeneration += 1;
  const speakerDefault = profile !== 'audioCall';
  try {
    await AudioSession.configureAudio({
      android: { audioTypeOptions: AndroidAudioTypePresets.communication },
      ios: { defaultOutput: speakerDefault ? 'speaker' : 'earpiece' },
    });
    if (Platform.OS === 'ios') {
      await AudioSession.setAppleAudioConfiguration({
        audioCategory: 'playAndRecord',
        audioCategoryOptions: speakerDefault
          ? ['allowBluetooth', 'allowBluetoothA2DP', 'allowAirPlay', 'defaultToSpeaker']
          : ['allowBluetooth', 'allowBluetoothA2DP', 'allowAirPlay'],
        audioMode: profile === 'videoCall' ? 'videoChat' : 'voiceChat',
      });
    }
    await AudioSession.startAudioSession();
    audioSessionActive = true;
  } catch (e) {
    console.warn('[CRYB] prepareVoiceAudio failed:', e);
  }
}

/**
 * Deactivate the audio session — SAFE + single-shot. Never call this while the
 * room is still tearing down: `setActive(false)` racing the WebRTC audio
 * engine's own stop is the known iOS EXC_BAD_ACCESS/double-stop crash family.
 * The screens call this only AFTER `room.disconnect()` has resolved (plus a
 * short settle delay).
 */
export async function stopVoiceAudio(): Promise<void> {
  if (!audioSessionActive) return;
  audioSessionActive = false;
  // Expose this stop as the in-flight teardown so a racing prepareVoiceAudio()
  // waits for it (prevents activate-vs-deactivate on the AVAudioSession). Cleared
  // in finally — but only if we're still the current teardown, so a later stop
  // that started while we were awaiting doesn't get its handle wiped.
  const run = (async () => {
    try {
      await AudioSession.stopAudioSession();
    } catch (e) {
      console.warn('[CRYB] stopVoiceAudio failed:', e);
    }
  })();
  teardownInProgress = run;
  try {
    await run;
  } finally {
    if (teardownInProgress === run) teardownInProgress = null;
  }
}

/**
 * Safety-net teardown for exits that bypass the screens' sequenced leave flow
 * (navigation pop / OS back). Waits `delayMs` so the <LiveKitRoom> unmount
 * disconnect can finish first, and no-ops if another live surface has since
 * re-prepared the session.
 */
export function scheduleVoiceAudioStop(delayMs = 1200): void {
  const gen = audioGeneration;
  setTimeout(() => {
    if (gen === audioGeneration) void stopVoiceAudio();
  }, delayMs);
}

// =============================================================================
// Timeout-guarded teardown (crash-on-close hardening)
//
// The screens' leave/end flow is: disconnect the LiveKit room → settle → stop
// the audio session → navigate away. Any of those native steps CAN hang (a
// stuck disconnect, a wedged AVAudioEngine stop). If we `await` a hung step the
// screen freezes on a black/white view and the user is trapped — which reads as
// a crash. `runGuardedTeardown` runs the teardown steps but resolves after at
// most MAX_TEARDOWN_MS no matter what, so the caller can ALWAYS force-navigate
// back. It also guarantees `scheduleVoiceAudioStop()` fires as a safety net, so
// the audio session is deactivated even if the inline stop never completed.
// The canonical audio SEQUENCE (disconnect → settle → stop) is preserved — the
// guard only bounds how long we wait on it.
// =============================================================================

/** Hard cap on how long a leave/end teardown may block navigation (ms). */
export const MAX_TEARDOWN_MS = 5000;

/** Settle delay between room.disconnect() and stopVoiceAudio() (ms). */
export const TEARDOWN_SETTLE_MS = 300;

/**
 * Run the canonical call/room teardown with a hard timeout guard.
 *
 * Steps (each wrapped so one failure never blocks the next):
 *   1. `disconnect()` — await the LiveKit room disconnect,
 *   2. settle delay so the native audio engine finishes stopping,
 *   3. `stopVoiceAudio()` — single-shot deactivate.
 *
 * The whole sequence races a MAX_TEARDOWN_MS timeout: if any step hangs, this
 * still resolves so the screen can force-navigate back (no white screen / trap).
 * `scheduleVoiceAudioStop()` is ALWAYS armed so the audio session is guaranteed
 * to be torn down even if the inline stop didn't finish before the timeout.
 */
export async function runGuardedTeardown(
  disconnect?: () => Promise<void> | void,
): Promise<void> {
  // Safety net FIRST: even if everything below hangs or throws, this delayed
  // stop still deactivates the session (and no-ops if a newer surface prepared
  // one). Guarantees scheduleVoiceAudioStop() always fires per the contract.
  try {
    scheduleVoiceAudioStop();
  } catch (e) {
    console.warn('[CRYB] scheduleVoiceAudioStop failed:', e);
  }

  const steps = (async () => {
    try {
      await disconnect?.();
    } catch (e) {
      console.warn('[livekit] disconnect failed:', e);
    }
    // Let the native audio engine finish stopping before deactivating the
    // session — deactivating mid-stop is the crash.
    try {
      await new Promise<void>((r) => { setTimeout(r, TEARDOWN_SETTLE_MS); });
    } catch {}
    try {
      await stopVoiceAudio();
    } catch (e) {
      console.warn('[livekit] stopVoiceAudio failed:', e);
    }
  })();

  // Whichever resolves first wins; the timeout guarantees we never block nav.
  await Promise.race([
    steps,
    new Promise<void>((resolve) => { setTimeout(resolve, MAX_TEARDOWN_MS); }),
  ]);
}

/**
 * Toggle speaker vs default routing (earpiece/headset) mid-call. iOS maps to
 * overrideOutputAudioPort; used by the audio-call speaker button.
 */
export async function setVoiceSpeaker(on: boolean): Promise<void> {
  try {
    if (Platform.OS === 'ios') {
      await AudioSession.selectAudioOutput(on ? 'force_speaker' : 'default');
    } else {
      await AudioSession.selectAudioOutput(on ? 'speaker' : 'earpiece');
    }
  } catch (e) {
    console.warn('[CRYB] setVoiceSpeaker failed:', e);
  }
}
